import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../lib/supabaseClient";
import {
    AccreditationRequest,
    AccreditationRequestFilters,
    AccreditationStatus,
    ReviewAccreditationPayload,
    ZoneType,
} from "../types/media";
import { useLeagueSeasonStore } from "@/state/useLeagueSeasonStore";
import { REVIEW_REQUEST_INVALIDATES } from "./mediaInvalidation";
import { mediaErrorFromBody, mediaErrorFromInvoke } from "./mediaInvokeError";
import { algiersToday, keepRowForDeletedHolder } from "./mediaDeletedLogic";

/** The part of a request row that names its holders (journalist, organisme)
 *  and its match (played or not). */
interface RequestHolderRow {
    status: string;
    match?: { status?: string | null; match_date?: string | null } | null;
    media_personnel?: {
        deleted_at?: string | null;
        organization?: { deleted_at?: string | null } | null;
    } | null;
}

/** Journalist + organisme of a request — the holders a deletion can hide. */
function requestHolders(row: RequestHolderRow) {
    return [row.media_personnel, row.media_personnel?.organization];
}

interface UseAccreditationRequestsOptions {
    filters?: AccreditationRequestFilters;
    enabled?: boolean;
}

interface AccreditationRequestListResult {
    data: AccreditationRequest[];
    total: number;
}

/**
 * Hook to fetch accreditation requests with filters
 * Scoped by the global league and season selection
 */
export function useAccreditationRequests(
    options: UseAccreditationRequestsOptions = {}
) {
    const { filters = {}, enabled = true } = options;
    const currentLeagueId = useLeagueSeasonStore((state) => state.currentLeagueId);
    const currentSeason = useLeagueSeasonStore((state) => state.currentSeason);

    return useQuery<AccreditationRequestListResult>({
        queryKey: ["accreditation-requests", currentLeagueId, currentSeason, filters],
        queryFn: async () => {
            let query = supabase
                .from("accreditation_requests")
                .select(
                    `
          *,
          media_personnel:media_personnel_id (
            id,
            media_type,
            press_card_number,
            press_card_url,
            photo_url,
            is_verified,
            first_name,
            last_name,
            first_name_ar,
            last_name_ar,
            email,
            phone,
            deleted_at,
            profile:profiles!profile_id (
              id,
              full_name,
              email,
              phone,
              avatar_url
            ),
            organization:media_organizations!organization_id (
              id,
              name,
              name_ar,
              org_type,
              logo_url,
              account_kind,
              deleted_at
            )
          ),
          league:leagues!league_id (
            id,
            name,
            name_ar
          ),
          match:matches!match_id (
            id,
            status,
            match_date,
            match_time,
            game_day,
            home_team:teams!home_team_id (name, name_ar),
            away_team:teams!away_team_id (name, name_ar),
            stadium:stadiums!stadium_id (
              name,
              name_ar,
              location_id,
              location:locations!fk_stadiums_location (
                wilaya,
                wilaya_ar,
                commune:communes,
                commune_ar
              )
            )
          ),
          qr_tokens:accreditation_qr_tokens (
            expires_at,
            is_revoked
          ),
          reviewer:profiles!accreditation_requests_reviewed_by_fkey (
            id,
            full_name
          )
        `,
                    { count: "exact" }
                )
                .order("created_at", { ascending: false });

            // Always filter by current league
            if (currentLeagueId) {
                query = query.eq("league_id", currentLeagueId);
            }

            // Filter by season if available
            if (currentSeason) {
                query = query.eq("season", currentSeason);
            }

            // Apply additional filters
            if (filters.status) {
                query = query.eq("status", filters.status);
            }

            if (filters.requestType) {
                query = query.eq("request_type", filters.requestType);
            }

            if (filters.dateFrom) {
                query = query.gte("created_at", filters.dateFrom);
            }

            if (filters.dateTo) {
                query = query.lte("created_at", filters.dateTo);
            }

            const { data, error, count } = await query;

            if (error) {
                throw new Error(error.message);
            }

            // A deleted holder keeps only its rows of played matches (any
            // status); its revoked upcoming ones leave the list and the count.
            const all = (data || []) as AccreditationRequest[];
            const today = algiersToday();
            const kept = all.filter((r) =>
                keepRowForDeletedHolder(r.match, requestHolders(r), today)
            );
            // The list is not paged: when every row came back, the kept rows
            // ARE the total. Only past PostgREST's row cap (10 000) does the
            // server count exceed the fetched rows; then subtract what was
            // hidden among those fetched.
            const total =
                all.length >= (count ?? 0)
                    ? kept.length
                    : Math.max(0, (count ?? 0) - (all.length - kept.length));
            return { data: kept, total };
        },
        enabled: enabled && !!currentLeagueId,
        staleTime: 30000, // 30 seconds
    });
}

/**
 * Mutation to review an accreditation request
 * Calls the review-accreditation-request edge function
 */
export function useReviewAccreditationRequest() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (payload: ReviewAccreditationPayload) => {
            const { data, error } = await supabase.functions.invoke(
                "review-accreditation-request",
                {
                    body: payload,
                }
            );

            // Keep the machine code (INVALID_VALIDITY_RANGE, …) so the views
            // can localize it with localizeMediaError.
            if (error) {
                throw await mediaErrorFromInvoke(error);
            }
            const bodyError = mediaErrorFromBody(data);
            if (bodyError) {
                throw bodyError;
            }

            return data;
        },
        onSuccess: () => {
            // A request is a child of a demand — refresh every cache that
            // renders it (see hooks/mediaInvalidation.ts).
            REVIEW_REQUEST_INVALIDATES.forEach((k) =>
                queryClient.invalidateQueries({ queryKey: [k] })
            );
        },
    });
}

/**
 * Get stats for accreditation requests dashboard
 */
export function useAccreditationRequestStats() {
    const currentLeagueId = useLeagueSeasonStore((state) => state.currentLeagueId);
    const currentSeason = useLeagueSeasonStore((state) => state.currentSeason);

    return useQuery({
        queryKey: ["accreditation-stats", currentLeagueId, currentSeason],
        queryFn: async () => {
            const { data, error } = await supabase
                .from("accreditation_requests")
                .select(
                    "status, match:matches!match_id(status, match_date), media_personnel:media_personnel_id(deleted_at, organization:media_organizations!organization_id(deleted_at))"
                )
                .eq("league_id", currentLeagueId!)
                .eq("season", currentSeason!);

            if (error) {
                throw new Error(error.message);
            }

            // Same rows as the list: a deleted holder only counts through its
            // rows of played matches (approved and rejected alike).
            const today = algiersToday();
            const rows = ((data || []) as unknown as RequestHolderRow[]).filter(
                (r) => keepRowForDeletedHolder(r.match, requestHolders(r), today)
            );

            // Count by status
            const stats = {
                total: rows.length,
                submitted: 0,
                underReview: 0,
                approved: 0,
                rejected: 0,
            };

            rows.forEach((item) => {
                switch (item.status) {
                    case AccreditationStatus.SUBMITTED:
                        stats.submitted++;
                        break;
                    case AccreditationStatus.UNDER_REVIEW:
                        stats.underReview++;
                        break;
                    case AccreditationStatus.APPROVED:
                        stats.approved++;
                        break;
                    case AccreditationStatus.REJECTED:
                    case AccreditationStatus.REVOKED:
                        stats.rejected++;
                        break;
                }
            });

            return stats;
        },
        enabled: !!currentLeagueId && !!currentSeason,
        staleTime: 60000, // 1 minute
    });
}
